import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Chat, Message, AIModel, Attachment, QueuedChatMessage } from '@/types';
import type { CollabConfig } from '@/components/chat/collabTypes';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { v4 as uuidv4 } from 'uuid';
import { sendMessageToModel, type AIConfig, type ModelThinking } from '@/services/ModelService';
import { useAppStore } from '@/store';
import { User } from '@supabase/supabase-js';
import { getProjectAiContext } from '@/lib/projectAiContext';
import { appendGemma4OnnxModel } from '@/lib/gemma4Onnx';
import { filterEnabledModels } from '@/lib/modelSettings';
import { getSuggestedTaskFromText, getTopModelsForTask } from '@/components/models/modelPresentation';
import { semanticSearch } from '@/lib/semanticSearch';
import {
  getDeepResearchNodes,
  normalizeCollabConfig,
  validateCollabConfig,
} from '@/components/chat/collabTypes';
import { runDeepResearchOrchestration } from '@/lib/deepResearch/orchestrator';
import type {
  DeepResearchAgentRole,
  DeepResearchKnowledgeBaseContext,
  DeepResearchSource,
} from '@/lib/deepResearch/types';

interface SupabaseChatState {
  chatId?: string;
  messages: Message[];
  isLoading: boolean;
  isGenerating: boolean;
  queuedMessages: QueuedChatMessage[];
  activeRequestId: string | null;
  activeRequestChatId: string | null;
  activeAssistantMessageId: string | null;
  stopRequestedForRequestId: string | null;
  activeAbortController: AbortController | null;
  error: string | null;
  chatTitle: string;
  modelName: string;
  selectedModelId: string | null;
  modelId2?: string | null;
  collabConfig?: CollabConfig | null;
  isUsingKnowledgeBase: boolean;
  isMemoryUpdating: boolean;
  initializeChat: (chatId: string) => void;
  sendMessage: (content: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null, user?: User, config?: AIConfig) => Promise<void>;
  removeQueuedMessage: (queuedMessageId: string) => void;
  stopActiveResponse: () => void;
  runDeepResearchCollab: (params: {
    content: string;
    attachments?: Attachment[];
    user: User;
    availableModels: AIModel[];
    currentProjectId?: string | null;
    knowledgeBaseId?: string | null;
    knowledgeBaseName?: string | null;
    collabConfig: CollabConfig;
  }) => Promise<void>;
  handleTitleChange: (newTitle: string) => void;
  handleDeleteChat: () => void;
  handleModelChange: (modelId: string) => void;
  handleModel2Change: (modelId: string) => void;
  saveCollabConfig: (config: CollabConfig | null) => Promise<void>;
  clearMessages: () => void;
  setMessages: (messages: Message[]) => void;
}

const MEMORY_THRESHOLD = 10;
const RECENT_MESSAGES_COUNT = 20;
const BILLABLE_PROVIDERS = new Set(['openai', 'claude', 'gemini', 'deepseek', 'cohere', 'maplept']);
const DEEP_RESEARCH_ROLES: DeepResearchAgentRole[] = [
  'orchestrator',
  'planner',
  'web_research',
  'academic_research',
  'code_analysis',
  'source_verification',
  'timeline_builder',
  'citation_manager',
  'report_writer',
  'knowledge_graph',
];
const ACADEMIC_SEARCH_DOMAINS = ['arxiv.org', 'doi.org', 'pubmed', 'acm.org', 'ieee.org', 'nature.com', 'springer.com'];

function sanitizeModelOutput(text: string): string {
  if (!text) return text;
  const userTurnIdx = text.search(/\n?\s*USER:/i);
  if (userTurnIdx >= 0) {
    text = text.slice(0, userTurnIdx);
  }
  const cleaned = text
    .split('\n')
    .filter((line) => !/^\s*(USER|ASSISTANT|SYSTEM):/i.test(line))
    .join('\n')
    .trim();
  return cleaned || text.trim();
}

function isBillableModel(model: AIModel) {
  return model.tier !== 'free' && BILLABLE_PROVIDERS.has(model.provider);
}

function estimateBillableTokens(messages: Message[], responseContent: string) {
  const messageCharacters = messages.reduce((total, message) => {
    if (typeof message.content === 'string') {
      return total + message.content.length;
    }

    if (Array.isArray(message.content)) {
      const contentParts = message.content as Array<{ text?: string }>;
      return total + contentParts.reduce((partTotal, part) => partTotal + (part.text?.length || 0), 0);
    }

    return total;
  }, 0);

  return Math.max(1, Math.ceil((messageCharacters + responseContent.length) / 4));
}

async function ensureTrackedUsage(params: {
  messages: Message[];
  responseContent: string;
  model: AIModel;
  chatId: string;
  runId: string;
  phase: string;
}) {
  const { messages, responseContent, model, chatId, runId, phase } = params;

  if (!isBillableModel(model)) {
    return;
  }

  const tokensUsed = estimateBillableTokens(messages, responseContent);
  if (tokensUsed <= 0) {
    return;
  }

  const { data, error } = await supabase.functions.invoke('track-ai-usage', {
    body: {
      provider: model.provider,
      model: model.name,
      modelTier: model.tier,
      tokensUsed,
      chatId,
      isWebLLM: false,
      isFreeModel: false,
      source: 'ai-chat',
      feature: 'chat',
      runId,
      phase,
    },
  });

  if (error) {
    console.warn('[useSupabaseChatStore] Failed to ensure tracked usage:', error);
    return;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mapleos:credits-refresh', { detail: data }));
  }
}

function buildAssistantThinkingMetadata(thinkingEnabled: boolean, thinking?: ModelThinking): Message['metadata'] | undefined {
  const shouldShowProviderThinking = Boolean(thinking?.providerExposed && thinking.text?.trim());

  if (!thinkingEnabled && !shouldShowProviderThinking) {
    return undefined;
  }

  return {
    thinking: thinking
      ? {
          enabled: true,
          status: thinking.status === 'none' ? 'unavailable' : thinking.status,
          text: thinking.text,
          providerExposed: thinking.providerExposed,
          supportsLiveStream: thinking.supportsLiveStream,
        }
      : {
          enabled: true,
          status: 'unavailable',
          providerExposed: false,
        },
  };
}

const deriveResearchOverallStatus = (phase: string, status: 'pending' | 'running' | 'complete' | 'failed', explicitStatus?: string) => {
  if (explicitStatus) return explicitStatus as 'researching' | 'verifying' | 'writing' | 'complete' | 'failed';
  if (status === 'failed') return 'failed';
  if (phase === 'planning' || phase === 'source_collection') return 'researching';
  if (phase === 'verification' || phase === 'citation_assembly') return 'verifying';
  if (phase === 'report_drafting') return 'writing';
  return 'complete';
};

async function loadKnowledgeBaseResearchContext(
  knowledgeBaseId: string | null | undefined,
  query: string,
  knowledgeBaseName?: string | null
): Promise<DeepResearchKnowledgeBaseContext | null> {
  if (!knowledgeBaseId) {
    return null;
  }

  try {
    const results = await semanticSearch(knowledgeBaseId, query, { limit: 4, threshold: 0.15 });
    if (!results.length) {
      return null;
    }

    const sources: DeepResearchSource[] = results.map((result, index) => ({
      id: `K${index + 1}`,
      title: result.name,
      snippet: result.content.slice(0, 700),
      type: 'knowledge_base',
      provider: 'knowledge_base',
      valid: true,
      qualityScore: 82,
      provenance: 'knowledge_base:semantic_search',
    }));

    return {
      id: knowledgeBaseId,
      name: knowledgeBaseName || 'Knowledge Base',
      summary: sources
        .map((source) => `- [${source.id}] ${source.title}\n${source.snippet}`)
        .join('\n\n'),
      sources,
    };
  } catch (error) {
    console.warn('[useSupabaseChatStore] Failed to load knowledge base context for deep research:', error);
    return null;
  }
}

async function searchKnowledgeBaseContext(
  knowledgeBaseId: string,
  query: string,
  knowledgeBaseName?: string | null
): Promise<{ hasResults: boolean; context?: string; message?: string }> {
  try {
    const results = await semanticSearch(knowledgeBaseId, query, { limit: 4, threshold: 0.15 });

    if (results.length > 0) {
      const context = [
        knowledgeBaseName ? `Use this knowledge base context from "${knowledgeBaseName}" when answering:` : 'Use this knowledge base context when answering:',
        ...results.map((result, index) => `Source ${index + 1}: ${result.name}\n${result.content.slice(0, 3000)}${result.content.length > 3000 ? '…' : ''}`),
      ].join('\n\n');

      return {
        hasResults: true,
        context,
      };
    }

    const { data: documents, error } = await supabase
      .from('knowledge_base_documents')
      .select('id', { count: 'exact' })
      .eq('knowledge_base_id', knowledgeBaseId)
      .limit(1);

    if (error) {
      throw error;
    }

    if (!documents || documents.length === 0) {
      return {
        hasResults: true,
        message: `The knowledge base${knowledgeBaseName ? ` "${knowledgeBaseName}"` : ''} is empty. Ask the user to add source documents first.`,
      };
    }

    return {
      hasResults: false,
    };
  } catch (error) {
    console.error('[useSupabaseChatStore] Knowledge base search failed:', error);
    return {
      hasResults: true,
      message: `There was an error searching the knowledge base: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

type QueuedMessageEntry = QueuedChatMessage & {
  userId: string;
  config?: AIConfig;
};

const createAbortError = () => new DOMException('The request was aborted.', 'AbortError');

const isAbortError = (error: unknown) =>
  Boolean(
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' && error !== null && 'name' in error && (error as { name?: string }).name === 'AbortError')
  );

function buildAssistantMessageMetadata(params: {
  thinkingEnabled?: boolean;
  thinking?: ModelThinking;
  generationStatus: 'streaming' | 'complete' | 'stopped' | 'error';
  stoppedByUser?: boolean;
  existing?: Message['metadata'];
}): Message['metadata'] | undefined {
  const { thinkingEnabled, thinking, generationStatus, stoppedByUser, existing } = params;
  const nextThinkingMetadata = thinkingEnabled
    ? buildAssistantThinkingMetadata(Boolean(thinkingEnabled), thinking)?.thinking
    : existing?.thinking
      ? {
          ...existing.thinking,
          status:
            generationStatus === 'streaming'
              ? existing.thinking.status
              : existing.thinking.providerExposed && existing.thinking.text?.trim()
                ? 'complete'
                : 'unavailable',
        }
      : undefined;

  const metadata: Message['metadata'] = {
    ...(existing || {}),
    generation: {
      status: generationStatus,
      stoppedByUser,
    },
  };

  if (nextThinkingMetadata) {
    metadata.thinking = nextThinkingMetadata;
  }

  return Object.keys(metadata).length > 0 ? metadata : undefined;
}

function getLocalChatMessage(chatId: string, messageId: string) {
  return useSupabaseStore.getState().chats.find((chat) => chat.id === chatId)?.messages?.find((message) => message.id === messageId);
}

function removeLocalMessages(chatId: string, messageIds: string[]) {
  useSupabaseStore.setState((state) => ({
    chats: state.chats.map((chat) =>
      chat.id === chatId
        ? {
            ...chat,
            messages: (chat.messages || []).filter((message) => !messageIds.includes(message.id)),
          }
        : chat
    ),
  }));
}

export const useSupabaseChatStore = create<SupabaseChatState>((set, get) => ({
  chatId: undefined,
  messages: [],
  isLoading: false,
  isGenerating: false,
  queuedMessages: [],
  activeRequestId: null,
  activeRequestChatId: null,
  activeAssistantMessageId: null,
  stopRequestedForRequestId: null,
  activeAbortController: null,
  error: null,
  chatTitle: '',
  modelName: '',
  selectedModelId: null,
  modelId2: null,
  collabConfig: null,
  isUsingKnowledgeBase: false,
  isMemoryUpdating: false,
  initializeChat: (chatId) => {
    const currentState = get();
    if (currentState.chatId && currentState.chatId !== chatId && currentState.isGenerating) {
      currentState.stopActiveResponse();
    }

    const state = useSupabaseStore.getState();
    const chat = state.chats.find(c => c.id === chatId);

    if (chat) {
      const model = state.models.find(m => m.id === chat.modelId);
      set({
        chatId,
        messages: chat.messages || [],
        queuedMessages: currentState.chatId === chatId ? currentState.queuedMessages : [],
        chatTitle: chat.title,
        selectedModelId: chat.modelId,
        modelId2: chat.modelId2,
        collabConfig: chat.collabConfig || null,
        modelName: model?.name || 'Unknown Model',
      });
    }
  },

  clearMessages: () => {
    set({ messages: [], queuedMessages: [] });
  },

  handleTitleChange: (newTitle) => {
    set({ chatTitle: newTitle });
    const { chatId } = get();
    if (chatId) {
      useSupabaseStore.getState().updateChat(chatId, { title: newTitle });
    }
  },

  handleDeleteChat: async () => {
    const { chatId, stopActiveResponse } = get();
    if (!chatId) return;

    stopActiveResponse();
    await useSupabaseStore.getState().deleteChat(chatId);
    set({
      chatId: undefined,
      messages: [],
      queuedMessages: [],
      chatTitle: '',
      selectedModelId: null,
      modelName: '',
      isLoading: false,
      isGenerating: false,
      activeRequestId: null,
      activeRequestChatId: null,
      activeAssistantMessageId: null,
      stopRequestedForRequestId: null,
      activeAbortController: null,
    });
  },

  handleModelChange: async (modelId) => {
    const { chatId } = get();
    if (!chatId) return;

    const model = useSupabaseStore.getState().models.find(m => m.id === modelId);
    if (!model) return;

    set({ selectedModelId: modelId, modelName: model.name });
    await useSupabaseStore.getState().updateChatModel(chatId, modelId);
  },

  handleModel2Change: async (modelId) => {
    const { chatId } = get();
    if (!chatId) return;

    set({ modelId2: modelId });
    await useSupabaseStore.getState().updateChat(chatId, { modelId2: modelId });
  },

  saveCollabConfig: async (config) => {
    const { chatId } = get();
    if (!chatId) return;

    set({ collabConfig: config });
    await useSupabaseStore.getState().updateChat(chatId, { collabConfig: config });
  },

  setMessages: (messages) => {
    set({ messages });
  },

  removeQueuedMessage: (queuedMessageId) => {
    set((state) => ({
      queuedMessages: state.queuedMessages.filter((queuedMessage) => queuedMessage.id !== queuedMessageId),
    }));
  },

  stopActiveResponse: () => {
    const {
      chatId,
      activeRequestId,
      activeAssistantMessageId,
      activeAbortController,
      stopRequestedForRequestId,
    } = get();

    if (!activeRequestId || stopRequestedForRequestId === activeRequestId) {
      return;
    }

    set({ stopRequestedForRequestId: activeRequestId });
    activeAbortController?.abort();

    if (!chatId || !activeAssistantMessageId) {
      return;
    }

    const currentAssistantMessage = getLocalChatMessage(chatId, activeAssistantMessageId);
    if (!currentAssistantMessage) {
      return;
    }

    useSupabaseStore.getState().updateMessageLocally(chatId, activeAssistantMessageId, {
      ...currentAssistantMessage,
      content: currentAssistantMessage.content === '...' ? '' : currentAssistantMessage.content,
      metadata: buildAssistantMessageMetadata({
        generationStatus: 'stopped',
        stoppedByUser: true,
        existing: currentAssistantMessage.metadata,
      }),
    });
  },

  runDeepResearchCollab: async ({
    content,
    attachments,
    user,
    availableModels,
    currentProjectId,
    knowledgeBaseId,
    knowledgeBaseName,
    collabConfig,
  }) => {
    const { chatId, selectedModelId } = get();
    const { addMessage: addMessageToDb, addMessageLocally, updateMessageLocally, addTokensUsed } = useSupabaseStore.getState();

    if (!chatId) {
      throw new Error('Chat is not initialized.');
    }

    const normalizedConfig = normalizeCollabConfig(collabConfig, availableModels, selectedModelId || undefined);
    const validation = validateCollabConfig(normalizedConfig, availableModels);
    if (!validation.isValid) {
      throw new Error(validation.errors[0] || 'Deep Research setup is incomplete.');
    }

    const roleModels = Object.fromEntries(
      getDeepResearchNodes(normalizedConfig)
        .filter((node) => DEEP_RESEARCH_ROLES.includes(node.role as DeepResearchAgentRole))
        .map((node) => [node.role, availableModels.find((candidate) => candidate.id === node.modelId) || null])
    ) as Record<DeepResearchAgentRole, AIModel | null>;

    const missingRole = DEEP_RESEARCH_ROLES.find((role) => !roleModels[role]);
    if (missingRole) {
      throw new Error('One or more Deep Research roles no longer have an available model.');
    }

    const collabGroupId = uuidv4();
    const tempUserMessageId = uuidv4();
    const roleTempIds = Object.fromEntries(DEEP_RESEARCH_ROLES.map((role) => [role, uuidv4()])) as Record<DeepResearchAgentRole, string>;
    const rolePersisted = new Set<DeepResearchAgentRole>();
    let tokenTotal = 0;

    const tempUserMessage: Message = {
      id: tempUserMessageId,
      chatId,
      content,
      role: 'user',
      sender: user.id,
      timestamp: new Date().toISOString(),
      attachments,
      is_collab: true,
      collab_group_id: collabGroupId,
      collab_role: 'user_prompt',
      collab_order: 0,
      collab_status: 'complete',
    };

    addMessageLocally(chatId, tempUserMessage);

    DEEP_RESEARCH_ROLES.forEach((role, index) => {
      const model = roleModels[role]!;
      addMessageLocally(chatId, {
        id: roleTempIds[role],
        chatId,
        content: role === 'report_writer' ? 'Waiting for the final research report…' : `Queued ${role.replace(/_/g, ' ')} stage…`,
        role: 'assistant',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        model: model.id,
        is_collab: true,
        collab_group_id: collabGroupId,
        collab_role: role,
        collab_model: model.id,
        collab_order: index + 1,
        collab_status: 'pending',
        metadata: {
          research: {
            workflow: 'deep_research',
            researchRole: role,
            researchPhase: role === 'report_writer' ? 'report_drafting' : 'planning',
            overallStatus: 'researching',
          },
        },
      });
    });

    const getLocalRoleMessage = (role: DeepResearchAgentRole) => get().messages.find((message) => message.id === roleTempIds[role]);

    try {
      const persistedUserMessage = await addMessageToDb(chatId, {
        content,
        role: 'user',
        sender: user.id,
        attachments,
        is_collab: true,
        collab_group_id: collabGroupId,
        collab_role: 'user_prompt',
        collab_order: 0,
        collab_status: 'complete',
      });

      if (!persistedUserMessage) {
        throw new Error('Failed to save the Deep Research prompt.');
      }

      updateMessageLocally(chatId, tempUserMessageId, persistedUserMessage);

      const [projectAiContext, knowledgeBaseContext] = await Promise.all([
        getProjectAiContext(currentProjectId),
        loadKnowledgeBaseResearchContext(knowledgeBaseId, content, knowledgeBaseName),
      ]);

      await runDeepResearchOrchestration({
        prompt: content,
        modelsByRole: roleModels as Record<DeepResearchAgentRole, AIModel>,
        knowledgeBaseContext,
        projectContext: projectAiContext?.systemPrompt || null,
        webSearch: async (query, type) => {
          const { data, error } = await supabase.functions.invoke('web-search', { body: { query } });
          if (error) throw error;

          const organicResults = Array.isArray(data?.results?.organic_results) ? data.results.organic_results : [];
          return organicResults.slice(0, 6).map((result: any, index: number) => {
            const link = typeof result?.link === 'string' ? result.link : null;
            const domain = link ? (() => {
              try {
                return new URL(link).hostname;
              } catch {
                return null;
              }
            })() : null;
            const inferredType = type === 'academic' || (domain && ACADEMIC_SEARCH_DOMAINS.some((hint) => domain.includes(hint)))
              ? 'academic'
              : 'web';

            return {
              id: `${type === 'academic' ? 'A' : 'W'}${index + 1}`,
              title: result?.title || `${type === 'academic' ? 'Academic' : 'Web'} source ${index + 1}`,
              url: link,
              snippet: result?.snippet || result?.summary || '',
              type: inferredType,
              provider: 'web_search',
              domain,
              publishedAt: result?.date || null,
              valid: true,
              qualityScore: inferredType === 'academic' ? 88 : 72,
              provenance: `web-search:${query}`,
            } as DeepResearchSource;
          });
        },
        callModel: async ({ role, phase, model, systemPrompt, userPrompt }) => {
          const messagesForModel: Message[] = [
            {
              id: uuidv4(),
              chatId,
              role: 'system',
              sender: 'system',
              timestamp: new Date().toISOString(),
              content: systemPrompt,
            },
            {
              id: uuidv4(),
              chatId,
              role: 'user',
              sender: user.id,
              timestamp: new Date().toISOString(),
              content: userPrompt,
            },
          ];

          const response = await sendMessageToModel(messagesForModel, model, availableModels, {
            usageContext: {
              source: 'ai-chat',
              feature: 'deep-research',
              runId: `${collabGroupId}:${role}`,
              phase,
            },
          });
          tokenTotal += response.tokensUsed || 0;
          return {
            content: sanitizeModelOutput(response.content),
            tokensUsed: response.tokensUsed,
          };
        },
        onStageUpdate: async (update) => {
          const model = roleModels[update.role]!;
          const localMessage = getLocalRoleMessage(update.role);
          const nextStatus = update.status === 'failed' ? 'error' : update.status;
          const nextMetadata: Message['metadata'] = {
            research: {
              workflow: 'deep_research',
              researchRole: update.role,
              researchPhase: update.phase,
              overallStatus: deriveResearchOverallStatus(update.phase, update.status, typeof update.metadata?.overallStatus === 'string' ? update.metadata.overallStatus : undefined),
              confidenceScore: typeof update.metadata?.confidenceScore === 'number' ? update.metadata.confidenceScore : undefined,
              citationStatus: typeof update.metadata?.citationStatus === 'string' ? update.metadata.citationStatus as any : undefined,
              verificationStatus: typeof update.metadata?.verificationStatus === 'string' ? update.metadata.verificationStatus as any : undefined,
              sourceCount: typeof update.metadata?.sourceCount === 'number' ? update.metadata.sourceCount : undefined,
              isFinalReport: Boolean(update.metadata?.isFinalReport),
              limitations: Array.isArray(update.metadata?.limitations) ? update.metadata.limitations as string[] : undefined,
              sources: Array.isArray(update.metadata?.sources) ? update.metadata.sources as any : undefined,
              confidence: typeof update.metadata?.confidence === 'object' ? update.metadata.confidence as any : undefined,
              citationCheck: typeof update.metadata?.citationCheck === 'object' ? update.metadata.citationCheck as any : undefined,
            },
          };

          const nextMessage: Message = {
            ...(localMessage || {
              id: roleTempIds[update.role],
              chatId,
              role: 'assistant',
              sender: 'ai',
              timestamp: new Date().toISOString(),
              model: model.id,
            }),
            content: update.content || localMessage?.content || '',
            model: model.id,
            is_collab: true,
            collab_group_id: collabGroupId,
            collab_role: update.role,
            collab_model: model.id,
            collab_order: DEEP_RESEARCH_ROLES.indexOf(update.role) + 1,
            collab_status: nextStatus,
            metadata: nextMetadata,
          };

          updateMessageLocally(chatId, roleTempIds[update.role], nextMessage);

          const shouldPersist = update.role === 'report_writer'
            ? Boolean(update.metadata?.isFinalReport)
            : update.role === 'orchestrator'
              ? update.phase === 'final_synthesis' || update.status === 'failed'
              : update.status === 'complete' || update.status === 'failed';
          if (!shouldPersist || update.status === 'running' || update.status === 'pending' || rolePersisted.has(update.role)) {
            return;
          }

          const persisted = await addMessageToDb(chatId, {
            content: nextMessage.content,
            role: 'assistant',
            sender: 'ai',
            model: model.id,
            metadata: nextMetadata,
            is_collab: true,
            collab_group_id: collabGroupId,
            collab_role: update.role,
            collab_model: model.id,
            collab_order: nextMessage.collab_order,
            collab_status: nextStatus,
          });

          if (persisted) {
            updateMessageLocally(chatId, roleTempIds[update.role], persisted);
            rolePersisted.add(update.role);
          }
        },
      });

      if (tokenTotal > 0) {
        await addTokensUsed(chatId, tokenTotal);
      }
    } catch (error) {
      useSupabaseStore.setState((state) => ({
        chats: state.chats.map((chat) =>
          chat.id === chatId
            ? {
                ...chat,
                messages: (chat.messages || []).filter(
                  (message) => ![tempUserMessageId, ...Object.values(roleTempIds)].includes(message.id)
                ),
              }
            : chat
        ),
      }));

      throw error;
    }
  },

  sendMessage: async (content, attachments, kbIdFromInput, kbNameFromInput, user, config) => {
    const state = get();
    const { chatId, selectedModelId, messages: currentMessages, isGenerating } = state;

    if (!user || !chatId || !selectedModelId) {
      toast({ title: 'Error', description: 'Cannot send message. User not logged in or chat not fully initialized.', variant: 'destructive' });
      set({ isLoading: false });
      return;
    }

    if (isGenerating) {
      const queuedMessage: QueuedMessageEntry = {
        id: uuidv4(),
        content,
        attachments,
        kbId: kbIdFromInput,
        kbName: kbNameFromInput,
        createdAt: new Date().toISOString(),
        userId: user.id,
        config,
      };

      set((currentState) => ({
        queuedMessages: [...(currentState.queuedMessages as QueuedMessageEntry[]), queuedMessage],
      }));

      return;

    }

    const models = filterEnabledModels(appendGemma4OnnxModel(useSupabaseStore.getState().models));
    const { addMessage: addMessageToDb, addMessageLocally, updateMessageLocally, updateChat } = useSupabaseStore.getState();
    const selectedModel = models.find(m => m.id === selectedModelId);

    if (!selectedModel) {
      toast({ title: 'Error', description: 'Selected AI model not found.', variant: 'destructive' });
      set({ isLoading: false });
      return;
    }

    const requestId = uuidv4();
    const abortController = new AbortController();
    const ensureRequestIsActive = () => {
      const currentState = get();
      if (
        abortController.signal.aborted ||
        currentState.activeRequestId !== requestId ||
        currentState.stopRequestedForRequestId === requestId
      ) {
        throw createAbortError();
      }
    };

    const hasImageAttachment = Boolean(attachments?.some((attachment) => attachment.type === 'image' || attachment.type === 'canvas'));
    const inferredTask = getSuggestedTaskFromText(content, hasImageAttachment);
    const preferredModels = getTopModelsForTask(models, inferredTask, models.length);
    const bestSuggestedModel = preferredModels[0];
    const selectedNeedsVisionFallback = inferredTask === 'vision' && !(selectedModel.supports_vision || selectedModel.capabilities?.includes('image'));
    const effectiveSelectedModel = selectedNeedsVisionFallback && bestSuggestedModel ? bestSuggestedModel : selectedModel;
    const thinkingEnabled = Boolean(config?.enableThinking && effectiveSelectedModel.supports_reasoning);

    if (selectedNeedsVisionFallback && bestSuggestedModel && bestSuggestedModel.id !== selectedModel.id) {
      toast({
        title: 'Auto-switched for compatibility',
        description: `Your selected model may not support images, so MapleOS used ${bestSuggestedModel.name} for this reply.`,
      });
    } else if (bestSuggestedModel && bestSuggestedModel.id !== selectedModel.id && inferredTask !== 'general') {
      toast({
        title: 'Model suggestion',
        description: `This request looks best suited for ${bestSuggestedModel.name}. You can switch any time from the selector.`,
      });
    }

    set({
      isLoading: true,
      isGenerating: true,
      error: null,
      activeRequestId: requestId,
      activeRequestChatId: chatId,
      activeAbortController: abortController,
      stopRequestedForRequestId: null,
    });

    let historyForAI: Message[] = [...currentMessages];
    let userQuery = content;
    const transientSystemMessages: Message[] = [];

    if (effectiveSelectedModel.provider === 'local') {
      transientSystemMessages.push({
        id: uuidv4(),
        chatId,
        role: 'system',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        content:
          'Strict output rules:\n' +
          '- Answer the user\'s last message directly.\n' +
          '- Do NOT include lines starting with USER:, ASSISTANT:, or SYSTEM:.\n' +
          '- Do NOT invent or simulate the user\'s reply.\n' +
          '- If you ask a follow-up question, end your message after the question and wait for a response.',
      });
    }

    const currentChat = useSupabaseStore.getState().chats.find(c => c.id === chatId);
    const projectAiContext = await getProjectAiContext(currentChat?.project_id);

    if (projectAiContext) {

      transientSystemMessages.push({
        id: uuidv4(),
        chatId,
        role: 'system',
        sender: 'system',
        timestamp: new Date().toISOString(),
        content: projectAiContext.systemPrompt,
      });
    }

    if (transientSystemMessages.length > 0) {
      historyForAI = [...transientSystemMessages, ...historyForAI];
    }

    const lastSummarizedCount = currentChat?.lastSummarizedTotalMessagesCount || 0;

    if (currentMessages.length - lastSummarizedCount >= MEMORY_THRESHOLD) {
      try {
        set({ isMemoryUpdating: true });
        const recentMessages = currentMessages.slice(-RECENT_MESSAGES_COUNT);
        const olderMessages = currentMessages.slice(0, -RECENT_MESSAGES_COUNT);

        if (olderMessages.length > 0) {
          const summaryPrompt: Message = {
            id: uuidv4(),
            chatId,
            role: 'system',
            sender: 'system',
            timestamp: new Date().toISOString(),
            content: 'Summarize the conversation so far in a concise but detailed way, preserving important facts, decisions, and unresolved questions.',
          };

          const summaryRequest = [...olderMessages, summaryPrompt];
          const summarizationModel = models.find(m => m.apiName === 'llama3') || effectiveSelectedModel;
          const summaryResponse = await sendMessageToModel(summaryRequest, summarizationModel, models, { signal: abortController.signal });
          ensureRequestIsActive();

          const summaryMessage: Message = {
            id: uuidv4(),
            chatId,
            role: 'system',
            sender: 'system',
            timestamp: new Date().toISOString(),
            content: `Conversation summary:\n${summaryResponse.content}`,
          };

          historyForAI = [summaryMessage, ...recentMessages];
          await updateChat(chatId, { lastSummarizedTotalMessagesCount: currentMessages.length });
        }
      } catch (memoryError) {
        if (!isAbortError(memoryError)) {
          console.error('Error updating memory:', memoryError);
        }
      } finally {
        set({ isMemoryUpdating: false });
      }
    }

    const knowledgeBaseId = kbIdFromInput || (currentChat as any)?.knowledge_base_id;

    if (knowledgeBaseId) {
      const searchResult = await searchKnowledgeBaseContext(knowledgeBaseId, content, kbNameFromInput || undefined);

      if (searchResult.hasResults) {

        set({ isUsingKnowledgeBase: true });
        if (searchResult.context || searchResult.message) {
          historyForAI.unshift({
            id: uuidv4(),
            role: 'system',
            content: searchResult.context || searchResult.message || '',
            sender: 'system',
            timestamp: new Date().toISOString(),
            chatId,
          });
        }
      } else {
        set({ isUsingKnowledgeBase: false });
      }
    }

    const lowerQuery = userQuery.toLowerCase();
    const wantsWebSearch =
      lowerQuery.startsWith('find me ') ||
      lowerQuery.startsWith('search for ') ||
      lowerQuery.includes('web search') ||
      lowerQuery.includes('google ') ||
      lowerQuery.match(/\b(find|search|lookup)\b.*\b(in|near|for)\b/);

    if (wantsWebSearch) {
      historyForAI.unshift({
        id: uuidv4(),
        chatId,
        role: 'system',
        sender: 'system',
        timestamp: new Date().toISOString(),
        content:
          'If the user\'s question would benefit from web search or reading a URL, output ONLY one JSON object:' +
          ' {"tool":"search_web","args":{"query":"..."} } or {"tool":"scrape_url","args":{"url":"..."} }.' +
          ' Do not include any other text before or after the JSON.',
      });
    }

    const tempUserMessageId = uuidv4();
    const userMessage: Message = {
      id: tempUserMessageId,
      chatId,
      content: userQuery,
      role: 'user',
      sender: user.id,
      timestamp: new Date().toISOString(),
      attachments,
    };

    historyForAI.push(userMessage);

    const tempAiMessageId = uuidv4();
    const usageRunBase = uuidv4();

    addMessageLocally(chatId, userMessage);
    addMessageLocally(chatId, {
      id: tempAiMessageId,
      chatId,
      content: '...',
      role: 'assistant',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      model: effectiveSelectedModel.id,
      metadata: buildAssistantMessageMetadata({
        thinkingEnabled,
        generationStatus: 'streaming',
      }),
    });

    set({ activeAssistantMessageId: tempAiMessageId });

    try {
      const shouldToolCheck = wantsWebSearch && effectiveSelectedModel.provider !== 'local';
      const primaryUsageContext = {
        source: 'ai-chat',
        feature: 'chat',
        runId: `${usageRunBase}:primary`,
        phase: shouldToolCheck ? 'tool-check' : 'response',
      };

      const toolCheckResponse = await sendMessageToModel(
        historyForAI,
        effectiveSelectedModel,
        models,
        {
          ...config,
          signal: abortController.signal,
          usageContext: primaryUsageContext,
        },
        shouldToolCheck
      );
      ensureRequestIsActive();

      let finalContent = sanitizeModelOutput(toolCheckResponse.content);
      let finalThinking = toolCheckResponse.thinking;
      let tokensUsed = toolCheckResponse.tokensUsed || 0;

      await ensureTrackedUsage({
        messages: historyForAI,
        responseContent: finalContent,
        model: effectiveSelectedModel,
        chatId,
        runId: primaryUsageContext.runId,
        phase: primaryUsageContext.phase,
      });
      ensureRequestIsActive();

      let toolCall: { tool?: string; args?: Record<string, any> } | null = null;
      if (shouldToolCheck) {
        const jsonMatch = finalContent.match(/\{[\s\S]*"tool"[\s\S]*\}/);
        if (jsonMatch?.[0]) {
          try {
            toolCall = JSON.parse(jsonMatch[0]);
          } catch (parseError) {
            console.error('Failed to parse tool call JSON:', parseError);
          }
        }
      }

      if (shouldToolCheck && toolCall?.tool && toolCall.args) {
        try {
          let toolResult = '';
          let inProgressMessage = '';

          if (toolCall.tool === 'search_web' && toolCall.args.query) {
            inProgressMessage = `Searching the web for "${toolCall.args.query}"...`;
          } else if (toolCall.tool === 'scrape_url' && toolCall.args.url) {
            inProgressMessage = `Reading content from ${toolCall.args.url}...`;
          } else {
            throw new Error('AI requested an invalid or incomplete tool.');
          }

          const currentAssistant = getLocalChatMessage(chatId, tempAiMessageId);
          if (currentAssistant) {
            updateMessageLocally(chatId, tempAiMessageId, {
              ...currentAssistant,
              content: inProgressMessage,
              metadata: buildAssistantMessageMetadata({
                generationStatus: 'streaming',
                existing: currentAssistant.metadata,
              }),
            });
          }

          if (toolCall.tool === 'search_web' && toolCall.args.query) {
            const { data, error } = await supabase.functions.invoke('web-search', { body: { query: toolCall.args.query } });
            if (error) throw new Error(error.message);
            ensureRequestIsActive();

            const searchResults = data.results;
            let summary = `Web search results for "${toolCall.args.query}":\n\n`;
            if (searchResults.organic_results && searchResults.organic_results.length > 0) {
              summary += searchResults.organic_results.slice(0, 5).map((r: any) => `Title: ${r.title}\nLink: ${r.link}\nSnippet: ${r.snippet}\n---`).join('\n');
            } else if (searchResults.answer_box) {
              summary += `Answer Box: ${searchResults.answer_box.snippet || searchResults.answer_box.answer}\n`;
            } else {
              summary = 'No relevant search results found.';
            }
            toolResult = summary;
          }

          if (toolCall.tool === 'scrape_url' && toolCall.args.url) {
            const { data, error } = await supabase.functions.invoke('scrape-url', { body: { url: toolCall.args.url } });
            if (error) throw new Error(error.message);
            ensureRequestIsActive();
            toolResult = `Content from ${toolCall.args.url}:\n\n${data.content}`;
          }

          const synthesisPrompt: Message = {
            id: uuidv4(),
            chatId,
            role: 'system',
            sender: 'system',
            content: `Here is the information you requested. Use it to answer the user's original question.\n\n${toolResult}`,
            timestamp: new Date().toISOString(),
          };
          const synthesisHistory = [...historyForAI, synthesisPrompt];
          const synthesisUsageContext = {
            source: 'ai-chat',
            feature: 'chat',
            runId: `${usageRunBase}:synthesis`,
            phase: 'synthesis',
          };
          const synthesisResponse = await sendMessageToModel(synthesisHistory, effectiveSelectedModel, models, {
            ...config,
            signal: abortController.signal,
            usageContext: synthesisUsageContext,
          });
          ensureRequestIsActive();

          finalContent = sanitizeModelOutput(synthesisResponse.content);
          finalThinking = synthesisResponse.thinking;
          tokensUsed += synthesisResponse.tokensUsed || 0;

          await ensureTrackedUsage({
            messages: synthesisHistory,
            responseContent: finalContent,
            model: effectiveSelectedModel,
            chatId,
            runId: synthesisUsageContext.runId,
            phase: synthesisUsageContext.phase,
          });
          ensureRequestIsActive();
        } catch (toolError: any) {
          if (isAbortError(toolError)) {
            throw toolError;
          }
          finalContent = `Error using tool: ${toolError.message}`;
          finalThinking = undefined;
        }
      }

      ensureRequestIsActive();

      const persistedUserMessage = await addMessageToDb(chatId, {
        content: userQuery,
        role: 'user',
        sender: user.id,
        attachments,
      });

      if (!persistedUserMessage) {
        throw new Error('Failed to save your message to the database.');
      }

      updateMessageLocally(chatId, tempUserMessageId, persistedUserMessage);

      const persistedAiMessage = await addMessageToDb(chatId, {
        content: finalContent,
        role: 'assistant',
        sender: 'ai',
        model: effectiveSelectedModel.id,
        metadata: buildAssistantMessageMetadata({
          thinkingEnabled,
          thinking: finalThinking,
          generationStatus: 'complete',
        }),
      });

      if (!persistedAiMessage) {
        throw new Error('Failed to save the AI response to the database.');
      }

      updateMessageLocally(chatId, tempAiMessageId, persistedAiMessage);

      if (tokensUsed > 0) {
        useSupabaseStore.getState().addTokensUsed(chatId, tokensUsed);
      }
    } catch (error: any) {
      if (isAbortError(error) || get().stopRequestedForRequestId === requestId) {
        const currentAssistant = getLocalChatMessage(chatId, tempAiMessageId);
        const partialContent = currentAssistant?.content === '...' ? '' : currentAssistant?.content || '';
        const stoppedMetadata = buildAssistantMessageMetadata({
          generationStatus: 'stopped',
          stoppedByUser: true,
          existing: currentAssistant?.metadata,
        });

        const persistedUserMessage = await addMessageToDb(chatId, {
          content: userQuery,
          role: 'user',
          sender: user.id,
          attachments,
        });

        if (persistedUserMessage) {
          updateMessageLocally(chatId, tempUserMessageId, persistedUserMessage);
        }

        const persistedStoppedMessage = await addMessageToDb(chatId, {
          content: partialContent,
          role: 'assistant',
          sender: 'ai',
          model: effectiveSelectedModel.id,
          metadata: stoppedMetadata,
        });

        if (persistedStoppedMessage) {
          updateMessageLocally(chatId, tempAiMessageId, persistedStoppedMessage);
        } else if (currentAssistant) {
          updateMessageLocally(chatId, tempAiMessageId, {
            ...currentAssistant,
            content: partialContent,
            metadata: stoppedMetadata,
          });
        }
      } else {
        console.error('Error sending message:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to send message. Please try again.',
          variant: 'destructive',
        });
        set({ error: error.message });
        removeLocalMessages(chatId, [tempUserMessageId, tempAiMessageId]);
      }
    } finally {
      const nextQueuedMessage = (get().queuedMessages[0] as QueuedMessageEntry | undefined) || undefined;

      set((currentState) => ({
        isLoading: false,
        isGenerating: false,
        activeRequestId: null,
        activeRequestChatId: null,
        activeAssistantMessageId: null,
        stopRequestedForRequestId: null,
        activeAbortController: null,
        queuedMessages: nextQueuedMessage
          ? currentState.queuedMessages.filter((queuedMessage) => queuedMessage.id !== nextQueuedMessage.id)
          : currentState.queuedMessages,
      }));

      if (nextQueuedMessage) {
        await get().sendMessage(
          nextQueuedMessage.content,
          nextQueuedMessage.attachments,
          nextQueuedMessage.kbId,
          nextQueuedMessage.kbName,
          { id: nextQueuedMessage.userId } as User,
          nextQueuedMessage.config
        );
      }
    }
  },
}));